import React, { createContext, useCallback, useContext, useState } from 'react';
import { getStartOfDay, getStartOfMonth } from 'views/widgets/date/date-utils';
import convertDateRange from './utils/convert-date-range';
import { translateToShortDateRange } from 'views/widgets/calendar/utils';
import { useAppContext } from 'core/appContext';
import Constants from 'core/constants';

const FilterContext = createContext();

const defaultDate = (dataSourceType) => {
  let dateType =
    dataSourceType === 'interaction-analytics' ? 'DATE_TIME' : 'DATE_ONLY';

  return {
    f: getStartOfMonth().format('YYYY-MM-DD'),
    l: getStartOfDay().format('YYYY-MM-DD'),
    c: 'G',
    r: 'M',
    t: dateType,
    n: 0,
  };
};

const getInitialDate = (config, clientId = -1) => {
  let savedDateRange = JSON.parse(localStorage.getItem('dataSourcesDateRange'));
  const dataSourceType = config?.pathIds?.datasources;

  if (!Object.keys(savedDateRange || {}).length) {
    savedDateRange = defaultDate(dataSourceType);
  } else if (dataSourceType === 'interaction-analytics') {
    savedDateRange = {
      ...savedDateRange,
      t: 'DATE_TIME',
      k: clientId,
    };
  } else {
    savedDateRange = {
      ...savedDateRange,
      t: 'DATE_ONLY',
      k: clientId,
    };
  }

  return savedDateRange;
};

const setUrlParam = (input) => {
  const pathName = window.location.pathname;
  if (!pathName.includes('datasources')) return;

  const params = new URLSearchParams(window.location.search);

  if (input?.length > 0) {
    params.set('filterId', input[0].filterId);
  } else {
    params.delete('filterId');
  }

  // Update the URL without reloading the page
  const newUrl = `${window.location.pathname}?${params
    .toString()
    .replace(/%3A/g, ':')}`;
  window.history.replaceState({}, '', newUrl);
};

const FilterContextProvider = ({ config, children }) => {
  const { client } = useAppContext();
  const [filter, setFilter] = useState(null);
  /**
   * we will save the date from dashboard and datasource in here
   * if individual card date is edited it will also contain its date
   * ex. { calendarType: 'G', ...andmore, card: {11095: { c: 'G', k: -1, ...andsoon, selectedDateId: 'custom'}}}
   */
  const [dateFilter, setDateFilter] = useState(
    getInitialDate(config, client?.clientId)
  );

  const setSavedFilter = useCallback(
    (payload) => {
      setUrlParam(payload);
      if (config?.routeName === Constants.ROUTE_NAME_LABELS.DATA_SOURCES) {
        setFilter(payload);
      }
    },
    [config?.routeName]
  );

  /**
   * lets use the raw dates in datasources so we dont need to convert it in every component
   */
  const setSavedDateFilter = useCallback(
    (payload) => {
      // TODO: check grahpql implementation why timezone is not needed
      let { __typename, z, ...dateRange } = payload;
      if (dateRange?.['calendarType']) {
        // remove this once we cleanup the old calendar
        dateRange = convertDateRange({ dateRange, shortHand: true });
      } else {
        dateRange = translateToShortDateRange(dateRange);
      }
      dateRange = { ...dateRange, k: client?.clientId };
      localStorage.setItem('dataSourcesDateRange', JSON.stringify(dateRange));
      setDateFilter(dateRange);
    },
    [client?.clientId]
  );

  return (
    <FilterContext.Provider
      value={{
        filter,
        setSavedFilter,
        dateFilter,
        setSavedDateFilter,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

const useFilterContext = () => {
  const context = useContext(FilterContext);

  if (context === undefined) {
    throw new Error(
      'useFilterContext must be used within a FiterContextProvider'
    );
  }
  return context;
};
export { FilterContext, FilterContextProvider, useFilterContext };
